import { InputFieldType } from "@/types";
import { OutlinedInput } from "@mui/material";
import { useTheme } from "@mui/system";
import { TimePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { useController, UseControllerProps } from "react-hook-form";
import CustomFormControl from "../FormControl/CustomFormControl";
import { CustomFormControlRequiredPropsForFields } from "../InputFields/inputfields.interface";
import { secondaryStyles } from "../InputFields/secondaryStyles";

export default function CustomTimePicker({
  name,
  control,
  label = null,
  error,
  timePickerProps,
  type = "primary",
  required = false,
  toolTip,
  disabled,
  extraButtons,
  placeholder,
  isReadOnly = false
}: CustomTimePickerProps) {
  const theme = useTheme();
  const { field } = useController({
    control,
    name,
  });

  return (
    <>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <TimePicker
          label={label}
          value={field.value ? field.value : null}
          onChange={(newValue) => {
            field.onChange(newValue);
          }}
          disabled={disabled}
          renderInput={(params) => {
            // Keep InputLabelProps out of `rest`, or it lands on the DOM node.
            const { InputProps, inputProps, InputLabelProps, ...rest } = params;

            return (
              <CustomFormControl
                label={label}
                required={required}
                error={error}
                toolTip={toolTip}
                disabled={disabled}
                extraButtons={extraButtons}
              >
                <OutlinedInput
                  {...(rest as any)}
                  {...InputProps}
                  placeholder={placeholder}
                  inputProps={{
                    ...inputProps,
                    // MUI injects its own format placeholder (e.g. "hh:mm (a|p)m", which reads as
                    // "(alp)m"); let an explicit placeholder win so we can show a clear hint.
                    ...(placeholder ? { placeholder } : {}),
                    readOnly: isReadOnly,
                  }}
                  helperText={error && typeof error === "string" ? error : ""}
                  FormHelperTextProps={{
                    error: true,
                  }}
                  sx={{ ...secondaryStyles(type, theme) }}
                />
              </CustomFormControl>
            );
          }}
          {...timePickerProps}
        />
      </LocalizationProvider>
    </>
  );
}

interface CustomTimePickerProps
  extends UseControllerProps,
  CustomFormControlRequiredPropsForFields {
  timePickerProps?: Record<any, any>;
  type?: InputFieldType;
  placeholder?: string;
  isReadOnly?: boolean
}
